import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, ExternalLink, FileDown, Handshake, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/lib/assets";

const imgSrc = (u) => (u?.startsWith("/api") ? `${API.replace("/api", "")}${u}` : u);

const PACKAGES = [
  {
    key: "gold",
    name: "Gold Sponsorship",
    price: "$1,000",
    term: "2 seasons",
    perks: [
      "Sole naming rights of a feature class event",
      "General Admission Family Pass for the night",
      "Logo in the nightly programme + social media promotion",
      "Your logo on winner sashes presented to competitors",
      "Pick your class or event to be named sponsor of",
    ],
    featured: true,
  },
  {
    key: "silver",
    name: "Silver Sponsorship",
    price: "$600",
    term: "1 season",
    perks: [
      "Sole naming rights of a feature class event",
      "General Admission tickets for 2 adults on the night",
      "Logo in the nightly programme + social media promotion",
      "Your logo on winner sashes presented to competitors",
    ],
    featured: false,
  },
];

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

const sponsorHref = (link) => {
  if (!link) return undefined;
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
};

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ business: "", contact_name: "", email: "", phone: "", message: "", first_time: false });

  useEffect(() => {
    api.get("/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.business.trim() && /.+@.+\..+/.test(form.email);
  const pkg = PACKAGES.find((p) => p.key === modal);
  const total = pkg ? parseInt(pkg.price.replace(/\D/g, ""), 10) + (form.first_time ? 350 : 0) : 0;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/sponsors/signup", {
        package: modal,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-clay-bg">
      <header className="border-b border-clay-border bg-clay-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <a data-testid="sponsors-logo" href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Mid Western Speedway logo" className="w-10 h-10 object-cover rounded-sm ring-1 ring-clay-border" />
            <span className="font-display font-bold uppercase text-lg tracking-wide">
              MWS <span className="text-clay-orange">Sponsors</span>
            </span>
          </a>
          <a data-testid="sponsors-back-link" href="/" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-gold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14 lg:py-20">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4">
          Partner With Mid Western Speedway
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-none mb-6">
          Fuel the <span className="text-clay-orange">Excitement.</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-clay-muted max-w-3xl leading-relaxed mb-4">
          As a volunteer-run local sporting club, we rely on the generous support of local businesses and individuals.
          In exchange you'll receive recognition on our Facebook page, your business logo up at our venue, a feature in
          our nightly race-day programs, and a link on this website — plus more.
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="text-clay-muted/70 text-sm max-w-3xl mb-14 italic">
          "We are immensely thankful to all of our past and present sponsors and what they have helped us accomplish
          over the years." — Chris Hay, President
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="border border-clay-gold/40 bg-clay-card p-6 sm:p-8 mb-14 flex flex-col sm:flex-row sm:items-center gap-6"
        >
          <div className="flex-1">
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl mb-2">
              Download the Sponsorship Booklet
            </h2>
            <p className="text-clay-muted text-sm leading-relaxed">
              The full 2026/27 proposal — packages, contacts and both race calendars — ready to print or share.
              The A4 version prints upright with normal settings; the booklet folds in half (print landscape, double-sided, flip on short edge).
            </p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <a
              data-testid="booklet-download-a4"
              href={`${API}/sponsorship/booklet.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-3.5 transition-colors"
            >
              <FileDown className="w-4 h-4" /> Easy Print PDF — A4
            </a>
            <a
              data-testid="booklet-download-booklet"
              href={`${API}/sponsorship/booklet.pdf?layout=booklet`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 border border-clay-orange text-clay-orange hover:bg-clay-orange hover:text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-3.5 transition-colors"
            >
              <FileDown className="w-4 h-4" /> Folded Booklet — A5
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.key}
              data-testid={`package-${p.key}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className={`border p-8 sm:p-10 relative overflow-hidden ${
                p.featured ? "border-clay-gold/50 bg-clay-card clay-glow" : "border-clay-border bg-clay-card"
              }`}
            >
              {p.featured && (
                <span className="absolute top-0 right-0 bg-clay-gold text-clay-bg font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5">
                  Best Value
                </span>
              )}
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay-orange mb-3">{p.term}</p>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mb-2">{p.name}</h2>
              <p className="font-display font-black text-5xl text-clay-gold mb-6">{p.price}</p>
              <ul className="space-y-2.5 mb-8">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-3 text-clay-text/90 text-sm">
                    <BadgeCheck className="w-4 h-4 text-clay-gold shrink-0 mt-0.5" />
                    {perk}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`package-${p.key}-button`}
                onClick={() => setModal(p.key)}
                className="w-full bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                <Handshake className="w-5 h-5" /> Become a {p.key === "gold" ? "Gold" : "Silver"} Sponsor
              </button>
              <a
                data-testid={`package-${p.key}-enquire`}
                href={`/?topic=${encodeURIComponent(p.name)}#contact`}
                className="w-full mt-3 border border-clay-border hover:border-clay-orange text-clay-muted hover:text-clay-text font-display font-bold uppercase tracking-wider px-8 py-3.5 transition-colors duration-300 flex items-center justify-center"
              >
                Enquire Today
              </a>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-center mt-3">
                First-time sponsors: +$350 sign production fee
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-20">
          {[
            ["Fence Signage", "Your business sign on the track fence all season long."],
            ["Sponsor a Race Meeting", "Put your business name on a full race night or feature event."],
            ["Sponsorship Donation", "Any amount — every dollar keeps the lights burning."],
          ].map(([title, desc]) => (
            <a
              key={title}
              data-testid={`enquire-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              href={`/?topic=${encodeURIComponent(title)}#contact`}
              className="group border border-clay-border bg-clay-surface/50 p-6 hover:border-clay-orange/60 transition-colors duration-300"
            >
              <h3 className="font-display font-bold uppercase text-xl tracking-tight mb-2 group-hover:text-clay-gold transition-colors">{title}</h3>
              <p className="text-clay-muted text-sm mb-3">{desc}</p>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-orange">Enquire →</span>
            </a>
          ))}
        </div>

        <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl leading-none mb-3">
          Our Track Sponsors
        </h2>
        <p className="text-clay-muted max-w-2xl mb-10">
          We value each and every one of our sponsors — click a logo to visit their website or Facebook page and show
          them some love, so they can continue to support us at the track.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {sponsors.map((s, i) => (
            <motion.a
              key={s.id}
              data-testid="sponsor-card"
              href={sponsorHref(s.link)}
              target={s.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
              className="group border border-clay-border bg-clay-card hover:border-clay-orange/60 transition-colors duration-300"
            >
              <div className="bg-white/95 h-36 sm:h-44 flex items-center justify-center p-5">
                <img src={imgSrc(s.logo)} alt={s.name} className="max-h-full max-w-full object-contain" loading="lazy" />
              </div>
              <div className="p-4 flex items-center justify-between gap-2">
                <span className="font-display font-bold uppercase tracking-wide text-sm group-hover:text-clay-gold transition-colors">{s.name}</span>
                {s.link && <ExternalLink className="w-4 h-4 text-clay-muted group-hover:text-clay-orange transition-colors shrink-0" />}
              </div>
            </motion.a>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {modal && pkg && (
          <motion.div
            data-testid="sponsor-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-clay-surface border border-clay-border p-6 sm:p-10 max-h-[92vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2">{pkg.name} — {pkg.term}</p>
                  <h3 className="font-display font-bold uppercase text-3xl tracking-tight">Sponsor Sign-Up</h3>
                </div>
                <button data-testid="sponsor-modal-close" onClick={() => setModal(null)} className="text-clay-muted hover:text-clay-text transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <input data-testid="sponsor-business-input" placeholder="Business name *" value={form.business} onChange={set("business")} className={inputCls} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="sponsor-contact-input" placeholder="Contact person" value={form.contact_name} onChange={set("contact_name")} className={inputCls} />
                  <input data-testid="sponsor-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
                <input data-testid="sponsor-email-input" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                <textarea data-testid="sponsor-message-input" placeholder="Anything we should know? (optional)" value={form.message} onChange={set("message")} rows={2} className={`${inputCls} resize-none`} />
                <label className="flex items-center gap-3 border border-clay-border bg-clay-bg px-4 py-3 cursor-pointer hover:border-clay-orange/60 transition-colors">
                  <input
                    data-testid="sponsor-firsttime-toggle"
                    type="checkbox"
                    checked={form.first_time}
                    onChange={(e) => setForm((f) => ({ ...f, first_time: e.target.checked }))}
                    className="w-4 h-4 accent-[#E65100]"
                  />
                  <span className="text-sm">
                    First-time sponsor
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-0.5">Adds the $350 sign production fee</span>
                  </span>
                </label>
                <div className="border border-clay-border bg-clay-bg p-4 flex justify-between font-mono text-sm">
                  <span className="text-clay-muted">Total today</span>
                  <span data-testid="sponsor-total" className="text-clay-gold font-bold">${total.toLocaleString()}.00 AUD</span>
                </div>
                <button
                  data-testid="sponsor-checkout-button"
                  disabled={!valid || loading}
                  onClick={submit}
                  className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 flex items-center justify-center gap-2 clay-glow"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Handshake className="w-5 h-5" />}
                  {loading ? "Redirecting…" : `Pay $${total.toLocaleString()} Securely`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
